import { apiClient } from './api-client';
import { 
  FeatureFlag, 
  CreateFeatureFlagDto, 
  UpdateFeatureFlagDto,
  PlanFeatureFlags,
  CustomerFeatureFlags,
  FeatureFlagFilters,
  FeatureFlagApiResponse,
  FeatureFlagJsonApiResource,
  FeatureFlagRoleOptions,
  RoleAccessPolicy,
  RoleAccessRule,
} from '@/types/feature-flags';

class FeatureFlagsService {
  /**
   * Normalize role rules to ensure consistent shape and default effect.
   */
  private normalizeRoleRules(policy?: RoleAccessPolicy): RoleAccessPolicy {
    const normalizeBucket = (rules?: RoleAccessRule[]) =>
      (rules ?? [])
        .filter((rule) => rule && rule.role)
        .map((rule) => ({
          role: rule.role,
          effect: rule.effect ?? 'allow',
          actions: rule.actions && rule.actions.length ? [...rule.actions] : undefined,
        }));

    return {
      provider: normalizeBucket(policy?.provider),
      customer: normalizeBucket(policy?.customer),
      team: normalizeBucket(policy?.team),
    };
  }

  /**
   * Prepare role rules for API payload:
   * - default effect to allow
   * - drop actions when all actions are allowed
   * - keep "manage" if selected (backend expands)
   */
  private serializeRoleRules(policy?: RoleAccessPolicy): RoleAccessPolicy | undefined {
    if (!policy) return undefined;

    const serializeBucket = (rules?: RoleAccessRule[]) =>
      (rules ?? [])
        .filter((rule) => rule && rule.role)
        .map((rule) => {
          const actions = rule.actions && rule.actions.length ? [...rule.actions] : undefined;
          return {
            role: rule.role,
            effect: rule.effect ?? 'allow',
            ...(actions ? { actions } : {}),
          };
        });

    return {
      provider: serializeBucket(policy.provider),
      customer: serializeBucket(policy.customer),
      team: serializeBucket(policy.team),
    };
  }

  private attachRoleRules(flag: FeatureFlag): FeatureFlag {
    return {
      ...flag,
      roleRules: this.normalizeRoleRules(flag.roleRules),
    };
  }

  /**
   * Transform JSON:API resource to frontend FeatureFlag type
   */
  private transformFeatureFlag(resource: FeatureFlagJsonApiResource): FeatureFlag {
    const featureFlag: FeatureFlag = {
      id: resource.id,
      key: resource.attributes.key,
      name: resource.attributes.name,
      description: resource.attributes.description,
      sortOrder: resource.attributes.sort_order,
      isVisible: resource.attributes.is_visible,
      helpText: resource.attributes.help_text,
      metadata: resource.attributes.metadata,
      createdAt: resource.attributes.created_at,
      updatedAt: resource.attributes.updated_at,
      roleRules: resource.attributes.role_rules ?? {},
      translations: {
        name: resource.attributes.translations.name,
        description: resource.attributes.translations.description,
        helpText: resource.attributes.translations.help_text,
      },
      planUsage: resource.meta?.plan_usage ? {
        subscriptionPlans: resource.meta.plan_usage.subscription_plans,
        oneTimePlans: resource.meta.plan_usage.one_time_plans,
        creditPackages: resource.meta.plan_usage.credit_packages,
        totalUsage: resource.meta.plan_usage.total_usage,
      } : undefined,
    };

    return this.attachRoleRules(featureFlag);
  }

  /**
   * Transform frontend DTO to JSON:API format
   */
  private transformToJsonApi(data: CreateFeatureFlagDto | UpdateFeatureFlagDto, flagId?: string) {
    const hasKey = typeof (data as CreateFeatureFlagDto).key === 'string' && (data as CreateFeatureFlagDto).key?.length;
    return {
      data: {
        type: 'product-feature-flags',
        ...(flagId ? { id: flagId } : {}),
        attributes: {
          ...(hasKey ? { key: (data as CreateFeatureFlagDto).key } : {}),
          name: data.name,
          description: data.description ?? null,
          'sort-order': data.sortOrder ?? 0,
          'is-visible': data.isVisible ?? true,
          'help-text': data.helpText,
          metadata: data.metadata ?? {},
          'role-rules': this.serializeRoleRules(data.roleRules),
          translations: data.translations ?? {},
        },
      },
    };
  }

  /**
   * Get feature flags with filtering and pagination
   */
  async getFeatureFlags(
    productId: string, 
    filters?: FeatureFlagFilters
  ): Promise<{ data: FeatureFlag[]; meta?: unknown; links?: unknown }> {
    const params = new URLSearchParams();
    
    if (filters?.isVisible !== undefined) {
      params.append('is_visible', String(filters.isVisible));
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.perPage) {
      params.append('per_page', String(filters.perPage));
    }
    if (filters?.page) {
      params.append('page', String(filters.page));
    }

    const url = `/api/v1/products/${productId}/feature-flags${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<FeatureFlagApiResponse>(url, {
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json',
      },
    });

    // Handle the response - check if it's already in the format we expect
    const responseData = response.data;
    
    // If LiveAdapter has already deserialized and transformed the response
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      const apiResponse = responseData as FeatureFlagApiResponse;
      
      // Check if data is an array of already-transformed feature flags
      if (Array.isArray(apiResponse.data)) {
        // If the first item doesn't have 'type' and 'attributes', it's already transformed
        const firstItem = apiResponse.data[0];
        if (!firstItem || (typeof firstItem === 'object' && !('type' in firstItem))) {
          // Already transformed by LiveAdapter
          return {
            data: (apiResponse.data as unknown as FeatureFlag[]).map((flag) => this.attachRoleRules(flag)),
            meta: apiResponse.meta,
            links: apiResponse.links,
          };
        } else {
          // Still in JSON:API format, transform it
          return {
            data: apiResponse.data.map(resource => this.transformFeatureFlag(resource)),
            meta: apiResponse.meta,
            links: apiResponse.links,
          };
        }
      } else {
        // Empty array or null
        return {
          data: [],
          meta: apiResponse.meta,
          links: apiResponse.links,
        };
      }
    }
    
    // If we get here, something unexpected happened, return empty
    return {
      data: [],
      meta: {},
      links: {},
    };
  }

  /**
   * Get a single feature flag
   */
  async getFeatureFlag(productId: string, flagId: string): Promise<FeatureFlag> {
    const response = await apiClient.get<FeatureFlagApiResponse>(
      `/api/v1/products/${productId}/feature-flags/${flagId}`,
      {
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'Accept': 'application/vnd.api+json',
        },
      }
    );

    const apiResponse = response.data as FeatureFlagApiResponse;
    const resource = apiResponse.data as FeatureFlagJsonApiResource | FeatureFlag;

    if (resource && typeof resource === 'object' && 'type' in resource) {
      return this.transformFeatureFlag(resource as FeatureFlagJsonApiResource);
    }

    return this.attachRoleRules(resource as FeatureFlag);
  }

  /**
   * Create a new feature flag
   */
  async createFeatureFlag(productId: string, data: CreateFeatureFlagDto): Promise<FeatureFlag> {
    const payload = this.transformToJsonApi(data);
    
    const response = await apiClient.post<FeatureFlagApiResponse>(
      `/api/v1/products/${productId}/feature-flags`,
      payload,
      {
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'Accept': 'application/vnd.api+json',
        },
      }
    );

    const responseData = response.data;
    
    // Check if LiveAdapter has already transformed the response
    if (responseData && typeof responseData === 'object' && 'id' in responseData && !('type' in responseData)) {
      // Already transformed by LiveAdapter, return as is (with proper typing)
      return this.attachRoleRules(responseData as unknown as FeatureFlag);
    } else {
      // Still in JSON:API format, transform it
      const apiResponse = responseData as FeatureFlagApiResponse;
      return this.transformFeatureFlag(apiResponse.data as FeatureFlagJsonApiResource);
    }
  }

  /**
   * Update a feature flag
   */
  async updateFeatureFlag(
    productId: string, 
    flagId: string, 
    data: UpdateFeatureFlagDto
  ): Promise<FeatureFlag> {
    const payload = this.transformToJsonApi(data, flagId);
    
    const response = await apiClient.patch<FeatureFlagApiResponse>(
      `/api/v1/products/${productId}/feature-flags/${flagId}`, 
      payload,
      {
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'Accept': 'application/vnd.api+json',
        },
      }
    );

    const responseData = response.data;
    
    // Check if LiveAdapter has already transformed the response
    if (responseData && typeof responseData === 'object' && 'id' in responseData && !('type' in responseData)) {
      // Already transformed by LiveAdapter, return as is (with proper typing)
      return this.attachRoleRules(responseData as unknown as FeatureFlag);
    } else {
      // Still in JSON:API format, transform it
      const apiResponse = responseData as FeatureFlagApiResponse;
      return this.transformFeatureFlag(apiResponse.data as FeatureFlagJsonApiResource);
    }
  }

  /**
   * Delete a feature flag
   */
  async deleteFeatureFlag(productId: string, flagId: string): Promise<void> {
    await apiClient.delete(`/api/v1/products/${productId}/feature-flags/${flagId}`, {
      headers: {
        'Accept': 'application/vnd.api+json',
      },
    });
  }

  /**
   * Legacy methods for plan feature flags (keeping for backward compatibility)
   * These may need to be updated later when the backend implements these features
   */
  async getPlanFeatureFlags(planId: string): Promise<PlanFeatureFlags> {
    const response = await apiClient.get<PlanFeatureFlags>(`/api/v1/billing/subscription-plans/${planId}/feature-flags`);
    return response.data;
  }

  async updatePlanFeatureFlags(
    planId: string, 
    featureFlags: { [key: string]: boolean }
  ): Promise<PlanFeatureFlags> {
    const response = await apiClient.put<PlanFeatureFlags>(
      `/api/v1/billing/subscription-plans/${planId}/feature-flags`,
      { featureFlags }
    );
    return response.data;
  }

  async getCreditPackageFeatureFlags(packageId: string): Promise<PlanFeatureFlags> {
    const response = await apiClient.get<PlanFeatureFlags>(`/api/v1/provider/credit-packages/${packageId}/feature-flags`);
    return response.data;
  }

  async updateCreditPackageFeatureFlags(
    packageId: string,
    featureFlags: { [key: string]: boolean }
  ): Promise<PlanFeatureFlags> {
    const response = await apiClient.put<PlanFeatureFlags>(
      `/api/v1/provider/credit-packages/${packageId}/feature-flags`,
      { featureFlags }
    );
    return response.data;
  }

  async getCustomerFeatureFlags(customerId: string): Promise<CustomerFeatureFlags> {
    const response = await apiClient.get<CustomerFeatureFlags>(`/api/v1/customers/${customerId}/feature-flags`);
    return response.data;
  }

  /**
   * Fetch available role/effect options for role rules
   */
  async getRoleOptions(productId: string): Promise<FeatureFlagRoleOptions> {
    const fallback: FeatureFlagRoleOptions = {
      provider: ['provider_owner', 'provider_admin', 'provider_staff'],
      customer: ['customer_owner', 'customer_member', 'customer_individual'],
      team: ['owner', 'admin', 'member'],
      effects: ['allow', 'deny'],
      actions: ['view', 'create', 'update', 'delete', 'manage'],
    };

    try {
      const response = await apiClient.get<{ data: FeatureFlagRoleOptions }>(
        `/api/v1/products/${productId}/feature-flags/options`,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      const options = response.data?.data;

      if (!options) return fallback;

      return {
        provider: options.provider?.length ? options.provider : fallback.provider,
        customer: options.customer?.length ? options.customer : fallback.customer,
        team: options.team?.length ? options.team : fallback.team,
        effects: options.effects?.length ? options.effects : fallback.effects,
        actions: options.actions?.length ? options.actions : fallback.actions,
      };
    } catch (error) {
      // If the options endpoint fails, fall back to the documented defaults so the UI stays usable.
      console.error('Failed to load feature flag role options, using defaults:', error);
      return fallback;
    }
  }
}

export const featureFlagsService = new FeatureFlagsService();
